/**
 * An open valuation project: its figures, its assumptions, and what it is worth.
 *
 * A valuation project is not a financial model, so it does not get the model
 * workspace. It has no statements, no balance check and no schedules - it has six
 * numbers a year and a discount rate. Showing it through a sixteen-tab shell would
 * be showing fifteen empty tabs.
 *
 * Three things are on screen that a model's valuation tab does not need, and they
 * are all about provenance:
 *
 *   - WHERE the figures came from, in words, on every valuation.
 *   - For a linked one, WHEN they were pinned, because they are a copy rather than a
 *     live read. A number that has been sent to somebody should stay that number.
 *   - WHAT HAS CHANGED in the accounts since, fetched before any refresh, so
 *     re-pinning is a decision rather than a surprise.
 */

import { useEffect, useState } from 'react';
import clsx from 'clsx';

import {
  useValuationStore,
  type PendingChange,
} from '../../../store/valuation-projects';
import type { Discipline, DisciplineId } from '../disciplines';
import ValuationMethodInputs, {
  type SuppliedMethod,
} from './ValuationMethodInputs';
import DisciplineSwitch from '../DisciplineSwitch';
import { Button } from '../ui/Button';
import ValuationExplanation, { type Explanation } from './ValuationExplanation';
import ValuationCharts from './ValuationCharts';
import { SaveState } from '../ui/SaveState';
import { WorkspaceShell, type ShellGroup } from '../ui/WorkspaceShell';

const FIELD_LABELS: Record<string, string> = {
  ebitda: 'EBITDA',
  capex: 'Capex',
  workingCapital: 'Working capital',
  taxPaid: 'Tax paid',
  cash: 'Cash',
  debt: 'Debt',
};

function money(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function Figure({ label, value, lead }: { label: string; value: unknown; lead?: boolean }) {
  // The equity value leads: it is the answer the other three explain.
  return (
    <div
      className={
        lead
          ? 'rounded-xl border border-accent-200 bg-accent-50 px-4 py-3'
          : 'rounded-xl border border-gray-200 bg-white px-4 py-3'
      }
    >
      <div className={lead ? 'text-xs font-medium text-accent-700' : 'text-xs text-gray-500'}>{label}</div>
      <div className="mt-1 font-mono text-xl font-medium tabular-nums text-gray-900">
        {money(value)}
      </div>
    </div>
  );
}

/** Valuation's tabs, grouped as every discipline's are. */
const GROUPS: ShellGroup[] = [
  {
    id: 'valuation',
    label: 'Valuation',
    tabs: [
      { id: 'overview', label: 'Overview' },
      { id: 'how', label: 'How it was reached' },
      { id: 'cash-flows', label: 'Cash flows' },
      { id: 'methods', label: 'Other methods' },
      { id: 'charts', label: 'Charts' },
    ],
  },
  { id: 'admin', label: '', tabs: [{ id: 'source', label: 'Source' }] },
];

function Changes({ changes }: { changes: PendingChange[] }) {
  return (
    <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3">
      <div className="text-sm font-medium text-amber-900">
        The accounts have changed since this valuation was pinned
      </div>
      <p className="mt-1 text-xs text-amber-800">
        These figures are a copy, so the valuation below still uses what was pinned.
        Refreshing replaces them.
      </p>
      <table className="mt-2 w-full text-xs">
        <thead>
          <tr className="text-left text-amber-700">
            <th className="py-1 pr-3 font-medium">Year</th>
            <th className="py-1 pr-3 font-medium">Figure</th>
            <th className="py-1 pr-3 text-right font-medium">Pinned</th>
            <th className="py-1 text-right font-medium">Now</th>
          </tr>
        </thead>
        <tbody className="text-amber-900">
          {changes.slice(0, 12).map((change) => (
            <tr key={`${change.year}-${change.field}`}>
              <td className="py-0.5 pr-3">{change.year}</td>
              <td className="py-0.5 pr-3">
                {FIELD_LABELS[change.field] ?? change.field}
              </td>
              <td className="py-0.5 pr-3 text-right tabular-nums">
                {money(change.was)}
              </td>
              <td className="py-0.5 text-right tabular-nums">{money(change.now)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {changes.length > 12 && (
        <p className="mt-1 text-xs text-amber-700">
          …and {changes.length - 12} more.
        </p>
      )}
    </div>
  );
}

/** What the linked filing's text says, each with the effect on an assumption it proposes. Nothing
 *  moves until the user accepts - editing the size if they disagree - and an accepted effect is
 *  recorded with its signal. */
function FilingSignals() {
  const project = useValuationStore((s) => s.project);
  const decideSignal = useValuationStore((s) => s.decideSignal);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const q = project?.qualitative;
  if (!project || !q || q.signals.length === 0) return null;
  const accepted = new Map(q.effects.map((e) => [e.signalId, e]));
  const pct = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)} points`;
  return (
    <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4 text-sm" data-testid="filing-signals">
      <h3 className="font-semibold text-gray-900">What the filing says</h3>
      <p className="mt-0.5 text-xs text-gray-600">
        Sentences found in the linked filing, each with its page. Where one suggests a risk the cash flows do not
        carry, it proposes a premium on the discount rate - applied only if you accept it.
      </p>
      <ul className="mt-3 space-y-3">
        {q.signals.map((s) => {
          const effect = accepted.get(s.id);
          const dismissed = q.dismissed.includes(s.id);
          return (
            <li key={s.id} className={clsx('rounded-lg border p-3', effect ? 'border-accent-300 bg-accent-50' : 'border-gray-100')}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {s.title}
                    {s.page !== null && <span className="ml-2 text-xs font-normal text-gray-500">page {s.page}</span>}
                  </p>
                  <p className="mt-1 text-xs italic text-gray-700">&ldquo;{s.quote}&rdquo;</p>
                  {s.proposal && (
                    <p className="mt-1 text-xs text-gray-600">
                      Proposes {pct(s.proposal.delta)} on the discount rate. {s.proposal.why}
                    </p>
                  )}
                  {effect && <p className="mt-1 text-xs font-medium text-accent-700">Accepted: {pct(effect.delta)} on the discount rate.</p>}
                  {dismissed && <p className="mt-1 text-xs text-gray-500">Dismissed.</p>}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {s.proposal && !effect && !dismissed && (
                    <>
                      <span className="flex items-center gap-1 text-xs text-gray-600">
                        <input
                          aria-label={`Premium for ${s.title}, in points`}
                          value={editing[s.id] ?? (s.proposal.delta * 100).toFixed(1)}
                          onChange={(e) => setEditing({ ...editing, [s.id]: e.target.value })}
                          className="w-14 rounded border border-gray-300 px-1 py-0.5 text-right"
                        />
                        points
                      </span>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          const points = Number(editing[s.id] ?? s.proposal!.delta * 100);
                          void decideSignal(project.id, s.id, 'accepted', Number.isFinite(points) ? points / 100 : undefined);
                        }}
                      >
                        Accept
                      </Button>
                    </>
                  )}
                  {!effect && !dismissed && (
                    <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => void decideSignal(project.id, s.id, 'dismissed')}>
                      Dismiss
                    </button>
                  )}
                  {(effect || dismissed) && (
                    <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => void decideSignal(project.id, s.id, 'open')}>
                      Undo
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The diligence layer: the same valuation on the adjusted figures, with where they came
 *  from, and a button that takes it off. The figures above are the valuation's own. */
function DiligenceLayer() {
  const project = useValuationStore((s) => s.project);
  const removeDiligenceLayer = useValuationStore((s) => s.removeDiligenceLayer);
  const layer = (project?.computed as Record<string, any> | null)?.withDiligence as
    | {
        from: { name: string; sentAt: string };
        ebitdaAdjustments: Array<{ year: number; label: string; amount: number }>;
        netDebtItems: Array<{ label: string; amount: number }>;
        equityValue: number | null;
        enterpriseValue: number | null;
        netDebt: number | null;
        notes?: string[];
      }
    | undefined;
  if (!project || !layer) return null;
  const money = (v: number | null | undefined) =>
    v === null || v === undefined ? '-' : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return (
    <section className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="diligence-layer">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">With the due diligence layer from {layer.from.name}</h3>
          <p className="mt-0.5 text-xs text-gray-600">
            Sent {layer.from.sentAt.slice(0, 10)}. Equity value {money(layer.equityValue)}, enterprise value{' '}
            {money(layer.enterpriseValue)}, net debt {money(layer.netDebt)} - the valuation above is unchanged.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void removeDiligenceLayer(project.id)}
          className="shrink-0 rounded px-2 py-1 text-xs text-gray-700 ring-1 ring-inset ring-amber-300 hover:bg-amber-100"
        >
          Remove layer
        </button>
      </div>
      <ul className="mt-2 list-disc pl-5 text-xs text-gray-700">
        {layer.ebitdaAdjustments.map((a, i) => (
          <li key={`a${i}`}>
            EBITDA FY{a.year}: {a.label} {money(a.amount)}
          </li>
        ))}
        {layer.netDebtItems.map((n, i) => (
          <li key={`n${i}`}>
            Net debt: {n.label} {money(n.amount)}
          </li>
        ))}
      </ul>
      {(layer.notes ?? []).map((note) => (
        <p key={note} className="mt-2 text-xs text-amber-900">
          {note}
        </p>
      ))}
    </section>
  );
}

export default function ValuationWorkspace({
  discipline,
  onSwitchDiscipline,
}: {
  discipline: Discipline;
  onSwitchDiscipline: (id: DisciplineId) => void;
}) {
  const project = useValuationStore((s) => s.project);
  const changes = useValuationStore((s) => s.changes);
  const sourceMissing = useValuationStore((s) => s.sourceMissing);
  const isSaving = useValuationStore((s) => s.isSaving);
  const error = useValuationStore((s) => s.error);
  const dismissError = useValuationStore((s) => s.dismissError);
  const close = useValuationStore((s) => s.close);
  const loadChanges = useValuationStore((s) => s.loadChanges);
  const refresh = useValuationStore((s) => s.refresh);
  const unlink = useValuationStore((s) => s.unlink);
  const update = useValuationStore((s) => s.update);
  const exportWorkbook = useValuationStore((s) => s.exportWorkbook);

  const linked = project?.source.kind === 'linked' && project.source.modelId;

  // Fetched on open, not on a button, so the diff is visible before anyone decides
  // to refresh rather than after.
  useEffect(() => {
    if (project && linked) void loadChanges(project.id);
  }, [project?.id, linked, loadChanges, project]);

  const [rate, setRate] = useState<string>('');
  const [tab, setTab] = useState('overview');
  useEffect(() => {
    if (project) setRate(String(project.config.discountRate));
  }, [project?.id, project]);

  if (!project) return null;

  const computed = project.computed ?? {};
  const rows = (computed.annualCashFlows as Record<string, unknown>[]) ?? [];
  const warnings = (computed.warnings as string[]) ?? [];
  const headline = computed.headlineMethod as { method: string; label: string } | undefined;
  const dcf = computed.dcf as { equityValue?: number } | undefined;

  const panel = 'rounded-xl border border-gray-200 bg-white p-5';

  return (
    <WorkspaceShell
      discipline={discipline}
      backLabel="Valuations"
      onBack={close}
      title={project.name}
      // Where the figures came from, on every valuation. A number is worth little
      // without knowing what it was computed from.
      subtitle={
        <>
          {project.figures.origin || 'Figures entered by hand'}
          {project.source.units !== 'units' && ` · in ${project.source.units}`}
        </>
      }
      status={<SaveState isSaving={isSaving} isDirty={false} />}
      switcher={<DisciplineSwitch current={discipline} onSwitch={onSwitchDiscipline} />}
      groups={GROUPS}
      active={tab}
      onSelect={setTab}
      tools={
        <Button size="sm" variant="secondary" onClick={() => void exportWorkbook(project.id, project.name)}>
          Export Excel
        </Button>
      }
      banner={
        error && (
          <div className="mx-4 mt-3 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <span>{error}</span>
            <button type="button" onClick={dismissError} className="shrink-0 text-xs underline">
              Dismiss
            </button>
          </div>
        )
      }
    >
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        {/* A pinned valuation whose accounts have moved says so on every tab: the
            figures here are a copy, and the copy is now behind. */}
        {linked && changes && changes.length > 0 && <Changes changes={changes} />}

        {tab === 'overview' && (
          <>
            {/* What it is worth. */}
            <div className="grid gap-3 sm:grid-cols-4">
              <Figure label="Equity value" value={computed.equityValue} lead />
              <Figure label="Enterprise value" value={computed.enterpriseValue} />
              <Figure label="Net debt" value={computed.netDebt} />
              <Figure label="Per share" value={computed.impliedSharePrice} />
            </div>
            {/* A miner, a holder, a property company or a biotech is valued on its supplied
                method, not the cash flow forecast: say which, and keep the forecast's figure
                beside it. */}
            {headline && (
              <p className="mt-2 text-xs text-gray-600">
                Valued on {headline.label}. The cash flow forecast gives{' '}
                {money(dcf?.equityValue)} — shown for comparison, not the answer: a
                perpetuity is the wrong shape for this business.
              </p>
            )}

            {/* What a due diligence project sent: beside the valuation, never in it. */}
            <DiligenceLayer />

            {/* What the filing's text says, as effects the user accepts (Prompt 6). */}
            <FilingSignals />

            {/* What the engine refused to do, and why. A terminal value silently
                dropped is as misleading as a negative one shown. */}
            {warnings.length > 0 && (
              <ul className="mt-4 space-y-1.5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            )}

            {project.notes.length > 0 && (
              <ul className="mt-3 space-y-1.5 rounded-xl border border-gray-200 bg-white p-4 text-xs text-gray-700">
                {project.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )}

            {/* The assumption that moves the answer most, editable in place. */}
            <div className={`mt-6 ${panel}`}>
              <label className="text-sm font-semibold text-gray-900">
                Discount rate
                <div className="mt-2 flex items-center gap-2">
                  <input
                    value={rate}
                    onChange={(event) => setRate(event.target.value)}
                    inputMode="decimal"
                    className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-right font-mono text-sm tabular-nums focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100"
                  />
                  <Button
                    variant="primary"
                    disabled={isSaving}
                    onClick={() => {
                      const value = Number(rate);
                      if (!Number.isNaN(value) && value > 0) {
                        void update(project.id, { config: { discountRate: value } });
                      }
                    }}
                  >
                    {isSaving ? 'Valuing…' : 'Apply'}
                  </Button>
                </div>
                <span className="mt-1.5 block text-xs font-normal text-gray-500">
                  As a fraction: 0.12 is 12%.
                </span>
              </label>
            </div>
          </>
        )}

        {tab === 'how' && (
          <ValuationExplanation explanation={computed.explanation as Explanation | undefined} />
        )}

        {tab === 'cash-flows' && (
          /* The figures it was computed from, and the flows that follow. */
          <div className={`overflow-x-auto ${panel}`}>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="text-right text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold">Year</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold">EBITDA</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold">Capex</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold">Δ working capital</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold">Tax</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold">Free cash flow</th>
                  <th className="border-b border-gray-200 px-2 py-2 font-semibold">Discounted</th>
                </tr>
              </thead>
              <tbody className="text-right font-mono tabular-nums text-gray-800">
                {rows.map((row) => (
                  <tr key={String(row.year)} className="hover:bg-gray-50">
                    <td className="border-b border-gray-100 px-2 py-1.5 text-left font-sans font-medium">
                      {String(row.year)}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5">{money(row.ebitda)}</td>
                    <td className="border-b border-gray-100 px-2 py-1.5">{money(row.capex)}</td>
                    <td className="border-b border-gray-100 px-2 py-1.5">{money(row.changeInWorkingCapital)}</td>
                    <td className="border-b border-gray-100 px-2 py-1.5">{money(row.taxesPaid)}</td>
                    <td className="border-b border-gray-100 px-2 py-1.5 font-semibold text-accent-700">{money(row.fcff)}</td>
                    <td className="border-b border-gray-100 px-2 py-1.5">{money(row.presentValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && (
              <p className="py-4 text-sm text-gray-500">
                No cash flows yet — the figures could not support a valuation.
              </p>
            )}
          </div>
        )}

        {tab === 'charts' && (
          <ValuationCharts
            computed={computed}
            discountRate={project.config.discountRate}
            terminalGrowthRate={project.config.terminalGrowthRate}
          />
        )}

        {tab === 'methods' && (
          /* The methods that need figures no cash flow could produce. For most
             companies the discounted cash flow is the answer and these are a second
             opinion; for a REIT or an insurer it is the other way round. */
          <ValuationMethodInputs
            methodInputs={project.config.methodInputs as Record<string, unknown> | null}
            results={
              (project.computed?.suppliedMethods as SuppliedMethod[] | undefined) ?? []
            }
            isSaving={isSaving}
            onChange={(methodInputs) =>
              void update(project.id, { config: { methodInputs } })
            }
          />
        )}

        {tab === 'source' && (
          <div className={panel}>
            <h2 className="text-sm font-semibold text-gray-900">Where the figures come from</h2>
            <p className="mt-1 text-sm text-gray-600">
              {project.figures.origin || 'Figures entered by hand'}
            </p>
            {sourceMissing && (
              <p className="mt-3 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                The accounting project this was pinned from has been deleted. The
                figures are the ones it was valued on and are unaffected — but it can
                no longer be refreshed.
              </p>
            )}
            {linked && (
              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                <span className="text-gray-600">
                  Pinned from <strong>{project.source.modelName}</strong>
                  {project.source.modelKind === 'baseline' ? ' (Baseline Accounting)' : ' (IAS accounting)'}
                  {project.source.pinnedAt && ` on ${project.source.pinnedAt.slice(0, 10)}`}
                </span>
                <Button
                  variant="secondary"
                  disabled={isSaving || sourceMissing}
                  onClick={() => void refresh(project.id)}
                >
                  Refresh from the accounts
                </Button>
                <Button disabled={isSaving} onClick={() => void unlink(project.id)}>
                  Unlink, keeping these figures
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}
